/**
 * Input/output contracts for the staff-area notification callables
 * (ADR-196): `getStaffNotificationOptions`, `previewStaffNotification`,
 * `createStaffNotification`, `cancelStaffNotification`.
 *
 * The mobile staff area never writes `notifications` or `audiences`
 * directly. It sends these inputs; the server checks the caller's
 * `sendNotifications` capability, validates every reference against the
 * event, builds the audience and writes the doc. The existing
 * `sendNotification` trigger and `processScheduledNotifications` cron do
 * delivery unchanged.
 *
 * Targets are named audiences plus individual personIds. Phones send
 * personIds, not `aud_personal_*` ids, so the server can check each
 * person is on the event and create a missing personal audience.
 *
 * Links are structured (`none` / `event` / `session`), never free text:
 * the email CTA goes out from the Flat Rock shared mailbox, so the server
 * builds every URL itself.
 */

import { z } from 'zod';
import { notificationChannelSchema } from '../schemas/notification';
import { STAFF_NOTIFICATION_LIMITS } from '../constants/staffCapabilities';

const docIdSchema = z.string().min(1).max(200);

const channelsSchema = z
  .array(notificationChannelSchema)
  .min(1, 'Pick push, email, or both.')
  .max(2)
  .refine((channels) => new Set(channels).size === channels.length, {
    message: 'Each channel can be picked once.',
  });

export const staffNotificationTargetSchema = z
  .object({
    audienceIds: z.array(docIdSchema).max(STAFF_NOTIFICATION_LIMITS.maxAudienceIds),
    personIds: z.array(docIdSchema).max(STAFF_NOTIFICATION_LIMITS.maxPersonIds),
  })
  .strict()
  .refine((target) => target.audienceIds.length + target.personIds.length > 0, {
    message: 'Pick at least one group or person.',
  });

export type StaffNotificationTarget = z.infer<typeof staffNotificationTargetSchema>;

export const staffNotificationLinkSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }).strict(),
  z.object({ kind: z.literal('event') }).strict(),
  z.object({ kind: z.literal('session'), sessionId: docIdSchema }).strict(),
]);

export type StaffNotificationLink = z.infer<typeof staffNotificationLinkSchema>;

/** `localDateTime` is `YYYY-MM-DDTHH:mm` read in the event's timezone. */
export const staffNotificationScheduleSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('now') }).strict(),
  z
    .object({
      mode: z.literal('later'),
      localDateTime: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'Pick a date and time.'),
    })
    .strict(),
]);

export type StaffNotificationSchedule = z.infer<typeof staffNotificationScheduleSchema>;

// ---------------------------------------------------------------------------
// getStaffNotificationOptions — what the compose screen can link to.
// Internal users cannot list every session under the rules, so the
// server returns the event's sessions (title and start only).
// ---------------------------------------------------------------------------

export const getStaffNotificationOptionsInputSchema = z
  .object({ eventId: docIdSchema })
  .strict();

export type GetStaffNotificationOptionsInput = z.infer<
  typeof getStaffNotificationOptionsInputSchema
>;

export interface StaffNotificationSessionOption {
  id: string;
  title: string;
  /** Session start as an ISO-8601 UTC string, or null when unset. */
  startsAt: string | null;
}

export interface GetStaffNotificationOptionsResponse {
  success: true;
  /** IANA timezone the schedule's `localDateTime` is read in. */
  eventTimezone: string;
  /** Sorted by start time; sessions without a start sort last. */
  sessions: StaffNotificationSessionOption[];
}

// ---------------------------------------------------------------------------
// previewStaffNotification — the confirm step's counts. Writes nothing and
// returns counts only, no names or addresses.
// ---------------------------------------------------------------------------

export const previewStaffNotificationInputSchema = z
  .object({
    eventId: docIdSchema,
    channels: channelsSchema,
    target: staffNotificationTargetSchema,
  })
  .strict();

export type PreviewStaffNotificationInput = z.infer<
  typeof previewStaffNotificationInputSchema
>;

export interface PreviewStaffNotificationResponse {
  success: true;
  /** Distinct people the target resolves to. */
  recipientCount: number;
  /** Signed-in people with at least one device registered for push. */
  pushReachableCount: number;
  /** People with an email address on file (includes not-yet-signed-in). */
  emailReachableCount: number;
  /** People who have not signed in to the app yet (email only). */
  notInAppCount: number;
}

// ---------------------------------------------------------------------------
// createStaffNotification — write the notification (send now or schedule).
// ---------------------------------------------------------------------------

export const createStaffNotificationInputSchema = z
  .object({
    eventId: docIdSchema,
    /**
     * One id per compose attempt, reused on retry. The server derives the
     * notification doc id from it, so a double tap or a retry after a
     * dropped response can never send twice.
     */
    clientRequestId: z
      .string()
      .regex(/^[A-Za-z0-9-]{8,64}$/, 'Invalid request id.'),
    title: z
      .string()
      .trim()
      .min(1, 'Add a title.')
      .max(STAFF_NOTIFICATION_LIMITS.titleMax),
    body: z
      .string()
      .trim()
      .min(1, 'Add a message.')
      .max(STAFF_NOTIFICATION_LIMITS.bodyMax),
    channels: channelsSchema,
    /** Falls back to `title` when email is on and this is null or blank. */
    emailSubject: z
      .string()
      .trim()
      .max(STAFF_NOTIFICATION_LIMITS.emailSubjectMax)
      .nullable(),
    target: staffNotificationTargetSchema,
    link: staffNotificationLinkSchema,
    schedule: staffNotificationScheduleSchema,
  })
  .strict();

export type CreateStaffNotificationInput = z.infer<
  typeof createStaffNotificationInputSchema
>;

export interface CreateStaffNotificationResponse {
  success: true;
  notificationId: string;
  status: 'sent' | 'scheduled';
  /** ISO-8601 UTC send time for a scheduled notification, else null. */
  scheduledAt: string | null;
  recipientCount: number;
  /** True when this `clientRequestId` had already been used. */
  deduplicated: boolean;
}

// ---------------------------------------------------------------------------
// cancelStaffNotification — cancel a scheduled notification.
// ---------------------------------------------------------------------------

export const cancelStaffNotificationInputSchema = z
  .object({ notificationId: docIdSchema })
  .strict();

export type CancelStaffNotificationInput = z.infer<
  typeof cancelStaffNotificationInputSchema
>;

export interface CancelStaffNotificationResponse {
  success: true;
}
